/**
 * Derives primary / subtitle copy, elapsed time, and idle-based escalation
 * while the copilot stream is active. Used by ChatPanel (indicator + composer).
 */

import { useEffect, useState } from 'react';
import { useCopilotStore } from '../stores/useCopilotStore';

export interface CopilotStreamingStatus {
  isActive: boolean;
  primary: string;
  staleSubtitle: string | null;
  elapsedSec: number;
  idleSec: number;
  toolCallCount: number;
}

export function useCopilotStreamingStatus(): CopilotStreamingStatus {
  const isStreaming = useCopilotStore((s) => s.isStreaming);
  const streamingAction = useCopilotStore((s) => s.streamingAction);
  const streamingContent = useCopilotStore((s) => s.streamingContent);
  const streamingToolCallCount = useCopilotStore((s) => s.streamingToolCallCount);
  const streamingStartedAt = useCopilotStore((s) => s.streamingStartedAt);
  const streamingLastEventAt = useCopilotStore((s) => s.streamingLastEventAt);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isStreaming) return;
    // All display thresholds (4 s, 5 s, 10 s, 12 s) are whole seconds — 1 s resolution
    // is visually identical to 500 ms but fires half as many re-renders during streaming.
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isStreaming]);

  if (!isStreaming || streamingStartedAt === null || streamingLastEventAt === null) {
    return {
      isActive: false,
      primary: '',
      staleSubtitle: null,
      elapsedSec: 0,
      idleSec: 0,
      toolCallCount: 0,
    };
  }

  const now = Date.now();
  const elapsedSec = Math.max(0, Math.floor((now - streamingStartedAt) / 1000));
  const idleSec = Math.max(0, Math.floor((now - streamingLastEventAt) / 1000));

  const hasContent = Boolean(streamingContent?.trim());
  const rawAction = (streamingAction || '').trim();
  const nToolCalls = streamingToolCallCount;

  const isGenericThinking = rawAction === 'Thinking…' && !hasContent && nToolCalls === 0;

  let primary: string;
  if (rawAction) {
    if (isGenericThinking) {
      if (idleSec >= 10) primary = 'Still working on your question…';
      else if (idleSec >= 4) primary = 'Choosing next steps…';
      else primary = 'Thinking…';
    } else {
      primary = rawAction;
    }
  } else if (hasContent) {
    primary = 'Composing answer…';
  } else if (nToolCalls > 0) {
    primary = 'Working…';
  } else {
    if (idleSec >= 10) primary = 'Still working on your question…';
    else if (idleSec >= 4) primary = 'Choosing next steps…';
    else primary = 'Thinking…';
  }

  const thinkingEscalated =
    primary === 'Choosing next steps…' || primary === 'Still working on your question…';

  let staleSubtitle: string | null = null;
  if (idleSec >= 5 && !thinkingEscalated) {
    if (rawAction && !isGenericThinking) {
      staleSubtitle =
        idleSec >= 12
          ? 'Large queries and multi-step reasoning can take a little while.'
          : 'This step is taking longer than usual…';
    } else if (hasContent) {
      if (idleSec >= 10) staleSubtitle = 'Still composing…';
    } else if (nToolCalls > 0 && !hasContent) {
      staleSubtitle =
        idleSec >= 12
          ? 'Almost done with this stretch of work…'
          : 'Finishing the current step…';
    }
  }

  return {
    isActive: true,
    primary,
    staleSubtitle,
    elapsedSec,
    idleSec,
    toolCallCount: nToolCalls,
  };
}
